import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getReferralAnalytics } from "./api";

const RANGE_OPTIONS = [
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
  { label: "180 days", value: 180 },
];

function formatPercent(rate) {
  if (typeof rate !== "number" || Number.isNaN(rate)) {
    return "—";
  }

  return `${Math.round(rate * 1000) / 10}%`;
}

function formatCount(value) {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "—";
  }

  return value.toLocaleString();
}

function StatCard({ label, value, tone }) {
  return (
    <div className={`ref-analytics-card${tone ? ` ref-analytics-card-${tone}` : ""}`}>
      <span className="ref-analytics-value">{value}</span>
      <span className="ref-analytics-label">{label}</span>
    </div>
  );
}

/** Dependency-free trend bars sized by the max invited value in the window. */
function TrendBars({ trend }) {
  const max = useMemo(
    () => trend.reduce((acc, day) => Math.max(acc, day.invited || 0), 0),
    [trend],
  );

  if (trend.length === 0) {
    return <div className="ref-empty">No activity in this window.</div>;
  }

  return (
    <div className="ref-trend">
      {trend.map((day) => {
        const height = max > 0 ? Math.round(((day.invited || 0) / max) * 100) : 0;

        return (
          <div className="ref-trend-col" key={day.date} title={`${day.date}: ${day.invited} invited, ${day.signedUp} signed up`}>
            <div className="ref-trend-bar-track">
              <div className="ref-trend-bar" style={{ height: `${height}%` }} />
            </div>
            <span className="ref-trend-date">{day.date.slice(5)}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Prod-only referral analytics: funnel, rates, delivery/failure, UI clicks, and
 * a daily trend. Firestore is the funnel source of truth; PostHog powers UI
 * click counts and degrades gracefully when unconfigured.
 */
export default function ReferralAnalyticsPanel({ env }) {
  const [days, setDays] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getReferralAnalytics(env, { days });
      setAnalytics(data?.analytics || null);
    } catch (err) {
      setError(err?.message || "Failed to load analytics.");
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [env, days]);

  useEffect(() => {
    load();
  }, [load]);

  const funnel = analytics?.funnel || {};
  const rates = analytics?.rates || {};
  const uiClicks = analytics?.uiClicks || {};

  return (
    <section className="ref-analytics">
      <div className="ref-analytics-header">
        <div>
          <h2 className="ref-section-title">Referral analytics</h2>
          <p className="ref-section-subtitle">
            Production funnel, delivery, and UI engagement. Aggregate counts only.
          </p>
        </div>
        <div className="ref-analytics-controls">
          <div className="ref-range-toggle">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`ref-range-btn${days === option.value ? " ref-range-btn-active" : ""}`}
                onClick={() => setDays(option.value)}
                disabled={loading}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button type="button" className="ref-btn ref-btn-ghost" onClick={load} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {error ? <div className="ref-error">{error}</div> : null}

      {loading && !analytics ? (
        <div className="ref-empty">Loading analytics…</div>
      ) : analytics ? (
        <>
          {analytics.truncated ? (
            <p className="ref-preview-note">
              Showing a capped sample for this range; totals may be
              underreported. Narrow the date range for exact numbers.
            </p>
          ) : null}

          <div className="ref-analytics-grid">
            <StatCard label="Invited" value={formatCount(funnel.invited)} />
            <StatCard label="Accepted" value={formatCount(funnel.accepted)} />
            <StatCard label="Delivered" value={formatCount(funnel.delivered)} />
            <StatCard label="Signed up" value={formatCount(funnel.signedUp)} />
            <StatCard label="Converted" value={formatCount(funnel.converted)} tone="good" />
            <StatCard label="Rewards" value={formatCount(funnel.rewards)} tone="good" />
          </div>

          <div className="ref-analytics-grid">
            <StatCard label="Acceptance rate" value={formatPercent(rates.acceptanceRate)} />
            <StatCard label="Signup rate" value={formatPercent(rates.signupRate)} />
            <StatCard label="Conversion rate" value={formatPercent(rates.conversionRate)} />
            <StatCard label="Bounce/drop rate" value={formatPercent(rates.bounceRate)} tone="bad" />
          </div>

          <div className="ref-analytics-grid">
            <StatCard label="Bounced" value={formatCount(funnel.bounced)} tone="bad" />
            <StatCard label="Dropped" value={formatCount(funnel.dropped)} tone="bad" />
            <StatCard label="Send failed" value={formatCount(funnel.failed)} tone="bad" />
            <StatCard label="Follow-up 1" value={formatCount(funnel.followUp1)} />
            <StatCard label="Follow-up 2" value={formatCount(funnel.followUp2)} />
          </div>

          <div className="ref-analytics-split">
            <div className="ref-analytics-block">
              <h3 className="ref-analytics-subtitle">Daily invites</h3>
              <TrendBars trend={analytics.trend || []} />
            </div>

            <div className="ref-analytics-block">
              <h3 className="ref-analytics-subtitle">UI engagement (PostHog)</h3>
              {uiClicks.available === false ? (
                <div className="ref-muted ref-analytics-note">
                  PostHog query not configured — showing Firestore funnel only.
                </div>
              ) : (
                <ul className="ref-analytics-list">
                  {Object.entries(uiClicks.byEvent || {}).map(([event, count]) => (
                    <li key={event} className="ref-analytics-list-row">
                      <span className="ref-analytics-list-key">
                        {event.replace(/^referral_/, "").replace(/_/g, " ")}
                      </span>
                      <span className="ref-analytics-list-value">{formatCount(count)}</span>
                    </li>
                  ))}
                  {Object.keys(uiClicks.byEvent || {}).length === 0 ? (
                    <li className="ref-muted">No UI events in this window.</li>
                  ) : null}
                </ul>
              )}
            </div>
          </div>

          {Array.isArray(analytics.topReferrers) && analytics.topReferrers.length > 0 ? (
            <div className="ref-analytics-block">
              <h3 className="ref-analytics-subtitle">Top referrers</h3>
              <ul className="ref-analytics-list">
                {analytics.topReferrers.map((row) => (
                  <li key={row.organizationId} className="ref-analytics-list-row">
                    <span className="ref-analytics-list-key">
                      {row.organizationName || row.organizationId}
                    </span>
                    <span className="ref-analytics-list-value">{formatCount(row.invited)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : (
        <div className="ref-empty">No analytics available.</div>
      )}
    </section>
  );
}
